import React, { useEffect, useRef, useState } from 'react';
import { throttle } from 'lodash';
import { useScreenSlideIn } from '@/hooks/useScreenSlideIn';
import styles from './index.module.scss';

const SCROLL_CONTAINER = '.aixue-new-home';
import { useCarousel } from '@/hooks/useCarousel';
import card1Big from '@/assets/home/new-screen5-card-1-bg.png';
import card2Big from '@/assets/home/new-screen5-card-2-bg.png';
import card3Big from '@/assets/home/new-screen5-card-3-bg.png';
import card2Small from '@/assets/home/new-screen5-card-2-small.png';
import card3Small from '@/assets/home/new-screen5-card-3-small.png';
import dashedBg from '@/assets/home/new-screen5-dashed.png';
import buttonLeft1 from '@/assets/home/new-screen5-button-left1.png';
import buttonRight1 from '@/assets/home/new-screen5-button-right1.png';
import buttonRight2 from '@/assets/home/new-screen5-button-right2.png';
import { CARD_FRAMES, FRAME_DURATION } from '@/utils/sortedFrames';

function SequenceFrameAnimation({
  cardIndex,
  className,
}: {
  cardIndex: number;
  className?: string;
}) {
  const [frameIndex, setFrameIndex] = useState(0);
  const lastTimeRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const isPlayingRef = useRef(true);

  const framePaths = CARD_FRAMES[cardIndex];

  useEffect(() => {
    if (!framePaths?.length) return;

    const updateFrame = (currentTime: number) => {
      if (!isPlayingRef.current) {
        rafRef.current = null;
        return;
      }
      if (lastTimeRef.current === 0) lastTimeRef.current = currentTime;
      const elapsed = currentTime - lastTimeRef.current;
      if (elapsed >= FRAME_DURATION) {
        setFrameIndex((prev) => (prev + 1) % framePaths.length);
        lastTimeRef.current = currentTime;
      }
      rafRef.current = requestAnimationFrame(updateFrame);
    };

    isPlayingRef.current = true;
    rafRef.current = requestAnimationFrame(updateFrame);
    return () => {
      isPlayingRef.current = false;
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [cardIndex, framePaths]);

  if (!framePaths?.length) return null;

  return (
    <div
      className={className}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
      }}
    >
      {framePaths.map((src, i) => (
        <img
          key={i}
          src={src}
          alt=""
          loading="lazy"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'top right',
            opacity: frameIndex === i ? 1 : 0,
            pointerEvents: 'none',
          }}
        />
      ))}
    </div>
  );
}

export default function Screen5() {
  const [scene, setScene] = useState<'scene1' | 'scene2'>('scene1');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [shouldLoadSequenceFrames, setShouldLoadSequenceFrames] = useState(false);
  const prevIndexRef = useRef<number>(0);
  const goToSlideRef = useRef<((index: number) => void) | null>(null);
  const sceneRef = useRef<'scene1' | 'scene2'>('scene1');
  const handleSlideChangeRef = useRef<((newIndex: number) => void) | null>(null);

  const slideInRef = useScreenSlideIn({
    triggerSlideRatio: 0.1,
    triggerOnce: false,
    onTrigger: (rootEl) => {
      if (sceneRef.current !== 'scene1') return;
      rootEl.classList.add(styles['screen5-scene1-to-scene2']);
      setTimeout(() => {
        setScene('scene2');
        rootEl.classList.remove(styles['screen5-scene1-to-scene2']);
        if (goToSlideRef.current) goToSlideRef.current(0);
        prevIndexRef.current = 0;
      }, 400);
    },
  });

  useEffect(() => {
    const sectionEl = slideInRef.ref.current;
    if (!sectionEl) return;

    const screen4Section = sectionEl.previousElementSibling as HTMLElement | null;
    if (!screen4Section) return;

    const getScrollTarget = (): HTMLElement | Window => {
      const el = document.querySelector(SCROLL_CONTAINER) as HTMLElement | null;
      if (!el) return window;
      const style = window.getComputedStyle(el);
      const scrollable =
        (style.overflowY === 'auto' ||
          style.overflowY === 'scroll' ||
          style.overflow === 'auto' ||
          style.overflow === 'scroll') &&
        el.scrollHeight > el.clientHeight;
      return scrollable ? el : window;
    };

    const lastScrollTopRef = { current: 0 };

    const checkAndReset = () => {
      const target = getScrollTarget();
      const currentScrollTop =
        target instanceof Window
          ? (window.scrollY ?? document.documentElement.scrollTop)
          : target.scrollTop;
      const scrollingUp = currentScrollTop < lastScrollTopRef.current;
      lastScrollTopRef.current = currentScrollTop;

      if (!scrollingUp) return;

      const rect4 = screen4Section.getBoundingClientRect();
      let visibleTop: number;
      let visibleBottom: number;
      if (target instanceof Window) {
        visibleTop = 0;
        visibleBottom = window.innerHeight;
      } else {
        const cr = target.getBoundingClientRect();
        visibleTop = cr.top;
        visibleBottom = cr.bottom;
      }
      const visibleHeight = Math.min(rect4.bottom, visibleBottom) - Math.max(rect4.top, visibleTop);
      const screen4VisibleRatio = rect4.height > 0 ? Math.max(0, visibleHeight) / rect4.height : 0;
      if (screen4VisibleRatio >= 0.5) {
        setScene('scene1');
      }
    };

    const scrollTarget = getScrollTarget();
    lastScrollTopRef.current =
      scrollTarget instanceof Window
        ? (window.scrollY ?? document.documentElement.scrollTop)
        : scrollTarget.scrollTop;
    const throttled = throttle(checkAndReset, 80);
    scrollTarget.addEventListener('scroll', throttled, { passive: true });
    return () => {
      scrollTarget.removeEventListener('scroll', throttled);
      throttled.cancel();
    };
  }, []);

  useEffect(() => {
    const sectionEl = slideInRef.ref.current;
    if (!sectionEl) return;
    const screen4Section = sectionEl.previousElementSibling as HTMLElement | null;
    if (!screen4Section) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShouldLoadSequenceFrames(true);
        }
      },
      { rootMargin: '0px', threshold: 0 },
    );
    io.observe(screen4Section);
    return () => io.disconnect();
  }, []);

  const images = [
    { big: card1Big, small: card1Big },
    { big: card2Big, small: card2Small },
    { big: card3Big, small: card3Small },
  ];

  const { currentIndex, goToSlide, carouselRef } = useCarousel({
    totalItems: images.length,
    enableWheel: true,
    enableTouch: false,
    wheelThreshold: 3,
    wrap: false,
    onWheelSlideChange:
      scene === 'scene2' ? (newIndex) => handleSlideChangeRef.current?.(newIndex) : undefined,
  });

  useEffect(() => {
    goToSlideRef.current = goToSlide;
  }, [goToSlide]);

  useEffect(() => {
    sceneRef.current = scene;
  }, [scene]);

  useEffect(() => {
    const container = carouselRef.current;
    if (!container || scene !== 'scene2') return;

    window.history.pushState({ screen5: true }, '', window.location.href);

    const handleGestureStart = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleGestureChange = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleGestureEnd = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDocumentGestureStart = (e: Event) => {
      const target = e.target as Node;
      if (container.contains(target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleDocumentGestureChange = (e: Event) => {
      const target = e.target as Node;
      if (container.contains(target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleDocumentGestureEnd = (e: Event) => {
      const target = e.target as Node;
      if (container.contains(target)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleWheel = (e: WheelEvent) => {
      const deltaX = e.deltaX;
      const deltaY = e.deltaY;
      const isHorizontalScroll = Math.abs(deltaX) > Math.abs(deltaY);

      if (isHorizontalScroll && !e.defaultPrevented) {
        e.preventDefault();
      }
    };

    const handleSwipe = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handlePopState = (e: PopStateEvent) => {
      if (e.state && (e.state as any).screen5) {
        e.preventDefault?.();
        window.history.pushState({ screen5: true }, '', window.location.href);
      }
    };

    container.addEventListener('gesturestart', handleGestureStart, {
      passive: false,
      capture: true,
    });
    container.addEventListener('gesturechange', handleGestureChange, {
      passive: false,
      capture: true,
    });
    container.addEventListener('gestureend', handleGestureEnd, {
      passive: false,
      capture: true,
    });
    container.addEventListener('wheel', handleWheel, {
      passive: false,
      capture: false,
    });
    container.addEventListener(
      'swipe',
      handleSwipe as EventListener,
      {
        passive: false,
        capture: true,
      } as any,
    );

    document.addEventListener('gesturestart', handleDocumentGestureStart, {
      passive: false,
      capture: true,
    });
    document.addEventListener('gesturechange', handleDocumentGestureChange, {
      passive: false,
      capture: true,
    });
    document.addEventListener('gestureend', handleDocumentGestureEnd, {
      passive: false,
      capture: true,
    });
    window.addEventListener('popstate', handlePopState);

    return () => {
      container.removeEventListener('gesturestart', handleGestureStart, { capture: true });
      container.removeEventListener('gesturechange', handleGestureChange, { capture: true });
      container.removeEventListener('gestureend', handleGestureEnd, { capture: true });
      container.removeEventListener('wheel', handleWheel, { capture: false });
      container.removeEventListener(
        'swipe',
        handleSwipe as EventListener,
        { capture: true } as any,
      );
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('gesturestart', handleDocumentGestureStart, { capture: true });
      document.removeEventListener('gesturechange', handleDocumentGestureChange, { capture: true });
      document.removeEventListener('gestureend', handleDocumentGestureEnd, { capture: true });
    };
  }, [carouselRef, scene]);

  useEffect(() => {
    prevIndexRef.current = currentIndex;
  }, []);

  const TRANSITION_MS = 400;

  useEffect(() => {
    if (scene === 'scene2' && prevIndexRef.current !== currentIndex) {
      if (!isTransitioning) {
        setIsTransitioning(true);
      }
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        prevIndexRef.current = currentIndex;
      }, TRANSITION_MS);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, scene, isTransitioning]);

  const handleSlideChange = (newIndex: number) => {
    if (isTransitioning || newIndex === currentIndex) return;
    prevIndexRef.current = currentIndex;
    setIsTransitioning(true);
    setTimeout(() => {
      if (goToSlideRef.current) {
        goToSlideRef.current(newIndex);
      }
    }, TRANSITION_MS);
  };
  handleSlideChangeRef.current = handleSlideChange;

  const handleIndicatorClick = (index: number) => {
    handleSlideChange(index);
  };

  return (
    <section className={styles['screen5-cover']} ref={slideInRef.ref}>
      <div className={styles['screen5-wrap']}>
        {/* 仅在 Screen4 进入视口且 scene1 时加载序列帧图片，之后动效由 SequenceFrameAnimation 通过显示/隐藏完成 */}
        {shouldLoadSequenceFrames && scene === 'scene1' && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: -9999,
              width: 1,
              height: 1,
              overflow: 'hidden',
              pointerEvents: 'none',
            }}
          >
            {CARD_FRAMES.map((urls, cardIdx) =>
              urls.map((src, i) => <img key={`${cardIdx}-${i}`} src={src} alt="" loading="lazy" />),
            )}
          </div>
        )}

        <div
          className={`${styles['screen5-dashed-bg']} ${
            scene === 'scene2' ? styles['screen5-dashed-bg-hidden'] : ''
          }`}
          style={{ backgroundImage: `url(${dashedBg})` }}
        />

        <div className={styles['screen5-card-container']} ref={carouselRef}>
          {images.map((image, index) => {
            const isActive = currentIndex === index;
            const isPrev = prevIndexRef.current === index && isTransitioning;
            const isNext =
              currentIndex === index && isTransitioning && prevIndexRef.current !== index;

            if (scene === 'scene1') {
              if (index === 0) {
                return (
                  <div
                    key={index}
                    className={`${styles['screen5-card']} ${styles['screen5-card-center-scene1']}`}
                  >
                    <div className={styles['screen5-card-image-wrap']}>
                      <img
                        src={image.big}
                        alt=""
                        className={styles['screen5-card-image']}
                        loading="lazy"
                      />
                      <div
                        className={`${styles['screen5-card-video-wrap']} ${styles['screen5-card-video-wrap-1']}`}
                      >
                        {shouldLoadSequenceFrames ? (
                          <SequenceFrameAnimation
                            cardIndex={0}
                            className={styles['screen5-card-video']}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className={styles['screen5-card-text-overlay-scene1']}>
                      <h2 className={styles['screen5-card-title-scene1']}>爱学大模型</h2>
                      <p className={styles['screen5-card-subtitle-scene1']}>
                        国内领先的"音视频实时对话式AI教育大模型"
                      </p>
                      <div className={styles['screen5-card-content-scene1']}>
                        <p>
                          在头部的国产大语言模型基础上基于教学数据进行后训练以及强化学习，训练出具备完全实时教学对话能力的独家教育大模型。
                        </p>
                        <p>
                          基于上述“实时对话式AI教育大模型”，已实现“实时个性化”全链路AI学习产品，是行业里首个（且是唯一一个）能够支持万人以上一起学习、实时反馈的AI产品。
                        </p>
                      </div>
                    </div>
                  </div>
                );
              } else if (index === 1) {
                return (
                  <div
                    key={index}
                    className={`${styles['screen5-card']} ${styles['screen5-card-left-scene1']}`}
                  >
                    <img
                      src={image.small}
                      alt=""
                      className={styles['screen5-card-image']}
                      loading="lazy"
                    />
                    <div className={styles['screen5-card-text-overlay-scene1']}>
                      <h2 className={styles['screen5-card-title-scene1-center']}>爱学数字人</h2>
                      <p className={styles['screen5-card-subtitle-scene1-center']}>
                        多场景沉浸交互专家
                      </p>
                    </div>
                  </div>
                );
              } else if (index === 2) {
                return (
                  <div
                    key={index}
                    className={`${styles['screen5-card']} ${styles['screen5-card-right-scene1']}`}
                  >
                    <img
                      src={image.small}
                      alt=""
                      className={styles['screen5-card-image']}
                      loading="lazy"
                    />
                    <div className={styles['screen5-card-text-overlay-scene1']}>
                      <h2 className={styles['screen5-card-title-scene1-center']}>智能语音交互</h2>
                      <p className={styles['screen5-card-subtitle-scene1-center']}>
                        极致的拟真教学沟通(超拟真·强理解・零障碍)
                      </p>
                    </div>
                  </div>
                );
              }
            }

            if (scene === 'scene2') {
              if (!isTransitioning && !isActive) {
                return null;
              }
              if (isTransitioning && !isPrev && !isNext) {
                return null;
              }

              const shouldShowAsActive = isActive && !isTransitioning && !isNext;
              const cardClassName = `${styles['screen5-card']} ${styles['screen5-card-scene2']} ${
                shouldShowAsActive ? styles['screen5-card-active'] : ''
              } ${isPrev ? styles['screen5-card-shrinking'] : ''} ${
                isNext ? styles['screen5-card-expanding'] : ''
              }`;

              return (
                <div key={index} className={cardClassName}>
                  <div className={styles['screen5-card-image-wrap']}>
                    <img
                      src={image.big}
                      alt=""
                      className={styles['screen5-card-image']}
                      loading="lazy"
                    />
                    <div
                      className={`${styles['screen5-card-video-wrap']} ${styles[`screen5-card-video-wrap-${index + 1}`]}`}
                    >
                      {shouldLoadSequenceFrames ? (
                        <SequenceFrameAnimation
                          cardIndex={index}
                          className={styles['screen5-card-video']}
                        />
                      ) : null}
                    </div>
                  </div>
                  {index === 0 && (
                    <div className={styles['screen5-card-text-overlay']}>
                      <h2 className={styles['screen5-card-title']}>爱学大模型</h2>
                      <p className={styles['screen5-card-subtitle']}>
                        国内领先的“音视频实时对话式AI教育大模型”
                      </p>
                      <div className={styles['screen5-card-content']}>
                        <p>
                          在头部的国产大语言模型基础上基于教学数据进行后训练以及强化学习，训练出具备完全实时教学对话能力的独家教育大模型。
                        </p>
                        <p>
                          基于上述“实时对话式AI教育大模型”，已实现“实时个性化”全链路AI学习产品，是行业里首个（且是唯一一个）能够支持万人以上一起学习、实时反馈的AI产品。
                        </p>
                      </div>
                    </div>
                  )}
                  {index === 1 && (
                    <div className={styles['screen5-card-text-overlay']}>
                      <h2 className={styles['screen5-card-title']}>爱学数字人</h2>
                      <p className={styles['screen5-card-subtitle']}>多场景沉浸交互专家</p>
                      <div className={styles['screen5-card-content']}>
                        <p>
                          我们专注于AI数字人技术的创新，现已构建覆盖秒级数字人、3D高斯数字人与大模型数字人的全场景技术体系，打造出真人级、可扩展、可商用的数字人系统，在教学互动、内容创作、虚拟交互等场景成功落地。
                        </p>
                        <p>
                          数字人具备多模态感知能力：融合表情、情绪、姿态识别，实现情感化智能响应。
                        </p>
                      </div>
                    </div>
                  )}
                  {index === 2 && (
                    <div className={styles['screen5-card-text-overlay']}>
                      <h2 className={styles['screen5-card-title']}>爱学智能语音交互</h2>
                      <p className={styles['screen5-card-subtitle']}>
                        极致的拟真教学沟通（超拟真·强理解·零障碍）
                      </p>
                      <div className={styles['screen5-card-content']}>
                        <p>拟人化多情感TTS：根据学习表现，切换鼓励或指导语气</p>
                        <p>纯净沟通体验：声纹过滤杂音，精准判断回答</p>
                        <p>跨语言理解：中文/英文/方言混合识别</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            return null;
          })}
        </div>

        {scene === 'scene2' && (
          <>
            <button
              type="button"
              className={`${styles['screen5-button-left1']} ${isTransitioning ? styles['screen5-button-disabled'] : ''}`}
              onClick={() => handleSlideChange((currentIndex - 1 + images.length) % images.length)}
              disabled={isTransitioning}
              aria-label="上一张"
            >
              <img
                src={buttonLeft1}
                alt=""
                className={styles['screen5-button-img-default']}
                loading="lazy"
              />
              <img
                src={buttonRight2}
                alt=""
                className={`${styles['screen5-button-img-hover']} ${styles['screen5-button-left-hover']}`}
                loading="lazy"
              />
            </button>
            <button
              type="button"
              className={`${styles['screen5-button-right1']} ${isTransitioning ? styles['screen5-button-disabled'] : ''}`}
              onClick={() => handleSlideChange((currentIndex + 1) % images.length)}
              disabled={isTransitioning}
              aria-label="下一张"
            >
              <img
                src={buttonRight1}
                alt=""
                className={styles['screen5-button-img-default']}
                loading="lazy"
              />
              <img
                src={buttonRight2}
                alt=""
                className={styles['screen5-button-img-hover']}
                loading="lazy"
              />
            </button>
          </>
        )}

        {scene === 'scene2' && (
          <div className={styles['screen5-indicators']}>
            {images.map((_, index) => (
              <div
                key={index}
                className={`${styles['screen5-indicator-hitarea']} ${isTransitioning ? styles['screen5-indicator-hitarea-disabled'] : ''}`}
                onClick={() => handleIndicatorClick(index)}
                onKeyDown={(e) => e.key === 'Enter' && handleIndicatorClick(index)}
                role="button"
                tabIndex={0}
                aria-label={`指示器 ${index + 1}`}
              >
                <span
                  className={`${styles['screen5-indicator']} ${
                    currentIndex === index ? styles['screen5-indicator-active'] : ''
                  } ${isTransitioning ? styles['screen5-indicator-disabled'] : ''}`}
                  aria-hidden
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
